"use client";

import { useState } from "react";
import { getLanding } from "@/content/landing";
import { localePath, DEFAULT_LOCALE, type Locale } from "@/lib/i18n";

/** ⚠️ THE SIX RISKS ARE NOT DECLARED HERE ANY MORE. They were a second copy of the same six
 *  bullets the landing page shows, and two copies of a legal disclosure is exactly the pair that
 *  drifts — the modal's version had already gained "Bybit or Binance" that the page's had not.
 *  Both now read `getLanding(locale).risk.items`, so the text a user accepts is the text they were
 *  shown, in whichever language they were shown it. */

const COPY: Record<Locale, {
  title: string; lead: string; check: string; agree: string; cancel: string;
  close: string; noteA: string; terms: string; noteB: string; privacy: string;
}> = {
  en: {
    title: "Trading risk disclosure & agreement",
    lead: "Please read and accept before creating an account.",
    check:
      "I have read and understand the trading-risk disclosure, and I accept these terms.",
    agree: "Agree & continue",
    cancel: "Cancel",
    close: "Close",
    noteA: "You will also be asked to accept the full",
    terms: "Terms of Service",
    noteB:
      "in-app before subscribing to a master or connecting an exchange key. See also our",
    privacy: "Privacy Policy",
  },
  ht: {
    title: "Deklarasyon ak akò sou risk trading",
    lead: "Tanpri li epi aksepte anvan ou kreye yon kont.",
    check:
      "Mwen li epi mwen konprann deklarasyon risk trading lan, epi mwen aksepte kondisyon sa yo.",
    agree: "Dakò epi kontinye",
    cancel: "Anile",
    close: "Fèmen",
    noteA: "Y ap mande w tou pou w aksepte tout",
    terms: "Kondisyon Sèvis yo",
    noteB:
      "nan aplikasyon an anvan ou abòne ak yon mèt oswa konekte yon kle echanj. Gade tou",
    privacy: "Règleman Konfidansyalite nou",
  },
};


/**
 * Trading-risk disclosure the user must affirmatively accept before proceeding to sign up.
 * Purely a UX gate — the full Terms of Service are still enforced in-app (server-side) before
 * subscribing or connecting an exchange key. No backend calls.
 */
export function RiskAgreementModal({
  locale = DEFAULT_LOCALE,
  onAgree,
  onClose,
}: {
  locale?: Locale;
  onAgree: () => void;
  onClose: () => void;
}) {
  const [checked, setChecked] = useState(false);
  const c = COPY[locale];
  const risks = getLanding(locale).risk.items;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-xs"
      onClick={onClose}
    >
      <div
        className="panel bracketed max-h-[90vh] w-full max-w-lg space-y-4 overflow-y-auto"
        style={{ boxShadow: "0 30px 60px rgb(0 0 0 / 0.6)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="status-dot-warn status-dot" />
            <h2 className="font-semibold text-warn">
              {c.title}
            </h2>
          </div>
          <button
            className="text-muted hover:text-primary"
            onClick={onClose}
            aria-label={c.close}
          >
            ✕
          </button>
        </div>

        <p className="text-sm text-muted">
          {c.lead}
        </p>

        <ul className="space-y-3 text-sm text-muted">
          {risks.map((r) => (
            <li key={r.b}>
              <b className="text-primary">{r.b}</b> {r.t}
            </li>
          ))}
        </ul>

        <label className="flex cursor-pointer items-start gap-2.5 border-t border-border pt-4 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          {c.check}
        </label>

        <div className="flex gap-2">
          <button className="btn flex-1" disabled={!checked} onClick={onAgree}>
            {c.agree}
          </button>
          <button className="btn-ghost" onClick={onClose}>
            {c.cancel}
          </button>
        </div>
        {/* The documents are readable BEFORE agreeing, as of 2026-08-15 — until then this
            sentence promised terms that had no page to link to. */}
        <p className="text-[11px] text-muted">
          {c.noteA}{" "}
          <a
            href={localePath(locale, "/legal/terms")}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-primary"
          >
            {c.terms}
          </a>{" "}
          {c.noteB}{" "}
          <a
            href={localePath(locale, "/legal/privacy")}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-primary"
          >
            {c.privacy}
          </a>
          .
        </p>
      </div>
    </div>
  );
}
